import type {BuilderParams} from "../../themes/builder-constants";

import {Label, Tooltip, Typography} from "@heroui/react";
import {InfoIcon, LockIcon, LockOpenIcon} from "@project/icons-web";
import {useState} from "react";

import {useThemeBuilder} from "./theme-builder-context";

/* -------------------------------------------------------------------------------------------------
 * LockableLabel
 * Displays a label with an optional info tooltip and a lock/unlock button.
 * The lock button is shown when hovered or when the variable is locked.
 * -----------------------------------------------------------------------------------------------*/

interface LockableLabelProps {
  label: string;
  variable: keyof BuilderParams;
  tooltip?: string;
}

export function LockableLabel({label, tooltip, variable}: LockableLabelProps) {
  const [isHovered, setIsHovered] = useState(false);
  const {lockedKeys, toggleLock} = useThemeBuilder();
  const isLocked = lockedKeys.includes(variable);

  return (
    <div
      className="flex h-6 items-center gap-1"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Label>{label}</Label>

      {tooltip ? (
        <Tooltip closeDelay={0} delay={100}>
          <Tooltip.Trigger className="hidden xl:block">
            <InfoIcon className="text-muted size-4" tabIndex={0} />
          </Tooltip.Trigger>
          <Tooltip.Content>
            <Tooltip.Arrow />
            <Typography>{tooltip}</Typography>
          </Tooltip.Content>
        </Tooltip>
      ) : null}

      <Tooltip closeDelay={0} delay={100}>
        <Tooltip.Trigger>
          <button
            aria-label={isLocked ? "Unlock value" : "Lock value"}
            aria-pressed={isLocked}
            className={[
              "button button--icon-only button--ghost size-6 rounded-full",
              isHovered || isLocked ? "flex" : "hidden",
            ].join(" ")}
            type="button"
            onClick={() => toggleLock(variable)}
          >
            {isLocked ? <LockIcon className="size-4" /> : <LockOpenIcon className="size-4" />}
          </button>
        </Tooltip.Trigger>
        <Tooltip.Content>
          <Tooltip.Arrow />
          <Typography>{isLocked ? "Unlock" : "Lock"} value</Typography>
        </Tooltip.Content>
      </Tooltip>
    </div>
  );
}
